// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import type { ErrorHandler } from '@angular/core';
import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { LoggerService } from '@core/services/logger.service';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * App-wide fallback for errors that surface inside Angular's zone and aren't
 * handled anywhere else (template/pipe throws, uncaught errors in effects or
 * rxMethods, unguarded HTTP calls). Without this the default handler only
 * console.errors and the user is left staring at a frozen view.
 *
 * Two deliberate boundaries:
 *  - HttpErrorResponse is logged but NOT toasted here. The interceptor already
 *    logs API errors and call sites own their own user-facing toasts (project
 *    convention), so toasting again would double up on every handled failure.
 *  - ToastrService is resolved lazily via Injector rather than injected as a
 *    constructor/field dependency: ErrorHandler is instantiated very early in
 *    bootstrap, and eagerly pulling in other providers risks a cyclic-DI error.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private injector = inject(Injector);
  private logger = inject(LoggerService);

  handleError(error: unknown): void {
    // Angular sometimes wraps the real error (e.g. rejected promises) in a
    // `.rejection`; unwrap so logging and type checks see the actual cause.
    const cause = this.unwrap(error);

    this.logger.error('GlobalErrorHandler', cause);

    // Handled-elsewhere errors are logged above but not surfaced again.
    if (cause instanceof HttpErrorResponse) {
      return;
    }

    // Resolve Toastr lazily — see class doc for why.
    this.injector.get(ToastrService).error(GENERIC_ERROR_MESSAGE);
  }

  private unwrap(error: unknown): unknown {
    if (error && typeof error === 'object' && 'rejection' in error) {
      return (error as { rejection: unknown }).rejection;
    }
    return error;
  }
}
